// Routing and page shell. Every route is prerendered per locale (scripts/prerender.mjs); the client hydrates it.
import { useEffect, useState, type ReactNode } from 'react'
import Welcome from './Welcome'
import { ChecklistPill } from './ChecklistPill'
import { Deferred, lazyComponent } from './lazy'
import { Footer, Header, IndependenceBanner, TranslationBanner } from './shell'
import { useT } from './i18n'
import { localizePath, splitLocale } from './i18n/locales'
import { routeFor, sectionPage, to, type SectionId } from './routes'

// One chunk per page. main.tsx preloads the current page's chunk before hydrating, so first load never suspends.
const HomePage = lazyComponent(() => import('./pages/Home'))
const SetupPage = lazyComponent(() => import('./pages/Setup'))
const WhenPage = lazyComponent(() => import('./pages/When'))
const FreeSoftwarePage = lazyComponent(() => import('./pages/FreeSoftware'))
const GrantsPage = lazyComponent(() => import('./pages/Grants'))
const HowItWorksPage = lazyComponent(() => import('./pages/HowItWorks'))
const FaqPage = lazyComponent(() => import('./pages/Faq').then(m => ({ default: m.FaqPage })))
const PosPage = lazyComponent(() => import('./pages/Faq').then(m => ({ default: m.PosPage })))
const LinksPage = lazyComponent(() => import('./pages/Info').then(m => ({ default: m.LinksPage })))
const AboutPage = lazyComponent(() => import('./pages/Info').then(m => ({ default: m.AboutPage })))
const ChangelogPage = lazyComponent(() => import('./pages/Info').then(m => ({ default: m.ChangelogPage })))
const NotFoundPage = lazyComponent(() => import('./pages/Info').then(m => ({ default: m.NotFoundPage })))
const PAGES: Record<string, { preload: () => Promise<void> }> = {
  '/': HomePage, '/setup': SetupPage, '/when': WhenPage, '/free-software': FreeSoftwarePage, '/grants': GrantsPage, '/how-it-works': HowItWorksPage, '/pos-and-retail': PosPage,
  '/faq': FaqPage, '/links': LinksPage, '/about': AboutPage, '/changelog': ChangelogPage, '404': NotFoundPage,
}
/** Loads the chunk for a path (without locale prefix) so hydration can render it straight away. */
export const preloadPage = (path: string) => (PAGES[routeFor(path)?.path ?? '404']).preload()

/** Client-side navigation within one locale; switching language is a normal page load (it needs that locale's strings). */
function useRoute(ssrUrl?: string) {
  const [loc, setLoc] = useState(() => typeof window === 'undefined' ? { pathname: ssrUrl ?? '/', hash: '' } : { pathname: window.location.pathname, hash: window.location.hash })
  useEffect(() => {
    const sync = () => setLoc({ pathname: window.location.pathname, hash: window.location.hash })
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element).closest('a')
      if (!a || a.target === '_blank' || a.hasAttribute('hreflang')) return
      const href = a.getAttribute('href') ?? ''
      if (href.startsWith('#')) return // same-page anchor: let the browser scroll
      const [p] = href.split('#')
      const here = splitLocale(window.location.pathname).locale
      const there = splitLocale(p)
      if (there.locale !== here || !routeFor(there.path)) return
      e.preventDefault(); window.history.pushState(null, '', href); if (!href.includes('#')) window.scrollTo(0, 0); sync()
    }
    window.addEventListener('popstate', sync); window.addEventListener('hashchange', sync); document.addEventListener('click', onClick)
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener('hashchange', sync); document.removeEventListener('click', onClick) }
  }, [])
  return loc
}

/** Page-wide scroll feel: scroll position for the hero parallax, a progress line under the header, and fade-up reveals. */
function useScrollMotion(page: string) {
  useEffect(() => {
    const root = document.documentElement
    let raf = 0
    const update = () => {
      raf = 0
      const max = root.scrollHeight - window.innerHeight
      root.style.setProperty('--sy', String(Math.min(window.scrollY, 900)))
      root.style.setProperty('--prog', String(max > 0 ? window.scrollY / max : 0))
    }
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update) }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => { window.removeEventListener('scroll', onScroll); if (raf) cancelAnimationFrame(raf) }
  }, [])
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const els = document.querySelectorAll<HTMLElement>('main section > div > *')
    const io = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) } }), { threshold: 0.06, rootMargin: '0px 0px -6% 0px' })
    els.forEach(el => { el.classList.add('rv'); io.observe(el) })
    return () => io.disconnect()
  }, [page])
}

/** Sections that moved to another page (e.g. /#calculator, /free-software#grants) are sent to where they live now. */
function legacyRedirect(path: string, hash: string): string | null {
  const id = hash.replace(/^#/, '') as SectionId
  if (!(id in sectionPage) || sectionPage[id] === path) return null
  return to(id)
}

export default function App({ ssrUrl }: { ssrUrl?: string }) {
  const { t, locale } = useT()
  const { pathname, hash } = useRoute(ssrUrl)
  const { path } = splitLocale(pathname)
  const route = routeFor(path)
  const page = route?.path ?? '404'
  useScrollMotion(page)
  // Keep <title>, description and canonical in step with client-side navigation (prerendered HTML already has them).
  useEffect(() => {
    if (!route) return
    document.title = t(`meta.${route.slug}.title`)
    document.querySelector('meta[name="description"]')?.setAttribute('content', t(`meta.${route.slug}.description`))
    const canon = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (canon) canon.href = new URL(canon.href).origin + localizePath(route.path, locale)
  }, [route, locale, t])
  useEffect(() => {
    const moved = legacyRedirect(page, hash)
    if (moved) { window.history.replaceState(null, '', localizePath(moved, locale)); window.dispatchEvent(new PopStateEvent('popstate')); return }
    // Anchors work across pages: after the view switches, scroll to the requested section. The new page's chunk may
    // still be loading, so wait (up to 3 s) for the section to appear.
    const id = hash.replace(/^#/, '')
    if (!id) return
    let raf = 0
    const until = performance.now() + 3000
    const find = () => {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView()
      else if (performance.now() < until) raf = requestAnimationFrame(find)
    }
    find()
    return () => cancelAnimationFrame(raf)
  }, [page, hash, locale])
  const shell = (main: ReactNode) => <><IndependenceBanner path={page === '404' ? '/' : page} /><TranslationBanner path={page === '404' ? '/' : page} /><Header current={page} /><Deferred>{main}</Deferred><Footer />{page !== '/' && <ChecklistPill />}</>
  switch (page) {
    case '/setup': return shell(<SetupPage />)
    case '/when': return shell(<WhenPage />)
    case '/free-software': return shell(<FreeSoftwarePage />)
    case '/grants': return shell(<GrantsPage />)
    case '/how-it-works': return shell(<HowItWorksPage />)
    case '/pos-and-retail': return shell(<PosPage />)
    case '/faq': return shell(<FaqPage />)
    case '/links': return shell(<LinksPage />)
    case '/about': return shell(<AboutPage />)
    case '/changelog': return shell(<ChangelogPage />)
    case '404': return shell(<NotFoundPage />)
    default: return <><Welcome />{shell(<HomePage />)}</>
  }
}
